<template>
  <div class="role-matrix">
    <header class="role-matrix__header">
      <div>
        <p class="role-matrix__eyebrow">{{ $t('ADMIN') }}</p>
        <h1 class="role-matrix__title">{{ $t('roleMatrix_title') }}</h1>
      </div>
      <div class="role-matrix__actions">
        <b-button variant="bg" @click="openCreateRoleDialog">
          {{ $t('roleMatrix_createRole') }}
        </b-button>
        <b-button variant="bg" :disabled="pendingChangeCount === 0 || isSaving" @click="save">
          {{ saveButtonLabel }}
        </b-button>
      </div>
    </header>
    <p class="role-matrix__notice">{{ $t(noticeKey) }}</p>

    <div class="role-matrix__body">
      <div class="role-matrix__matrix">
        <MatrixHeader
          :roles="roles"
          :levels="levels"
          :level-labels="levelLabels"
          :focused-role-number="focus.roleNumber"
          @focus-role="focusRole"
          @edit-role="openRenameRoleDialog"
          @edit-members="openMembersDialog"
        />
        <div class="role-matrix__rows">
          <MatrixRow
            v-for="row in visibleRows"
            :key="row.node.id"
            :row="row"
            :roles="roles"
            :levels="levels"
            :label="nodeLabel(row.node)"
            :kind-label="kindLabel(row.node)"
            :is-collapsed="Boolean(collapsed[row.node.id])"
            :is-focused-row="focus.nodeId === row.node.id"
            :focused-role-number="focus.roleNumber"
            :cell-state="(role, level) => cellState(role, row.node.id, level)"
            :cell-title="(role, level) => cellTitle(role, row.node, level)"
            :cell-hint="(role) => cellHint(role, row.node.id)"
            @toggle-collapse="toggleCollapse"
            @select-cell="focusCell"
            @toggle-cell="toggleCell"
          />
        </div>
      </div>

      <RightPanel
        :level-options="levelOptions"
        :effective-level="focusedEffectiveLevel"
        :has-selection="Boolean(focusedRole && focus.nodeId)"
        :focused-role-name="focusedRole ? focusedRole.name : ''"
        :preview-rows="previewRows"
        :node-label="nodeLabel"
        :kind-label="kindLabel"
        @set-level="setFocusedLevel"
      />
    </div>

    <RoleDialog
      :visible.sync="roleDialog.isVisible"
      :mode="roleDialog.mode"
      :initial-name="roleDialog.role ? roleDialog.role.name : ''"
      @submit="submitRoleDialog"
      @delete="deleteRole"
    />
    <RoleMembersDialog
      :visible.sync="membersDialog.isVisible"
      :role-name="membersDialog.role ? membersDialog.role.name : ''"
      :users="organizationUsers"
      :member-ids="membersDialog.role ? membersDialog.role.members : []"
      @submit="submitMembers"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MatrixHeader from '@/components/RoleMatrix/MatrixHeader';
import MatrixRow from '@/components/RoleMatrix/MatrixRow';
import RightPanel from '@/components/RoleMatrix/RightPanel';
import RoleDialog from '@/components/RoleMatrix/RoleDialog';
import RoleMembersDialog from '@/components/RoleMatrix/RoleMembersDialog';
import {
  applyCellLevelChange,
  diffRoleAssignments,
  getCellState,
  getEffectiveLevel,
  getMenuPreviewRows,
  getVisibleRows,
  isMissingViewAssignment,
  toggleCellLevel,
} from '@/helpers/roleMatrix';

const LEVELS = ['viewer', 'editor'];

const CELL_STATE_LABEL_KEYS = {
  allow: 'roleMatrix_permissionGranted',
  partial: 'roleMatrix_permissionPartial',
  none: 'roleMatrix_permissionNone',
};

const TOAST_LIFE_MS = 3000;

export default {
  name: 'OrganizationRoleMatrix',
  components: { MatrixHeader, MatrixRow, RightPanel, RoleDialog, RoleMembersDialog },
  data() {
    return {
      levels: LEVELS,
      collapsed: {},
      focus: { roleNumber: null, nodeId: null },
      editedAssignments: {},
      isSaving: false,
      roleDialog: { isVisible: false, mode: 'create', role: null },
      membersDialog: { isVisible: false, role: null },
    };
  },
  computed: {
    ...mapGetters(['getAccessCatalog', 'getOrganizationRoles', 'getUsers']),
    ...mapGetters('auth', ['getCurrentUser', 'isOrganizationRoleNavigationEnabled']),
    noticeKey() {
      return this.isOrganizationRoleNavigationEnabled
        ? 'roleMatrix_effectiveNotice'
        : 'roleMatrix_notEffectiveNotice';
    },
    catalog() {
      return this.getAccessCatalog;
    },
    catalogNodeIds() {
      return new Set(this.catalog.map((node) => node.id));
    },
    roles() {
      return this.getOrganizationRoles;
    },
    organizationUsers() {
      const organizationId = this.organizationIdOf(this.getCurrentUser);
      return this.getUsers.filter((user) => this.organizationIdOf(user) === organizationId);
    },
    levelLabels() {
      return { viewer: this.$t('viewer'), editor: this.$t('editor') };
    },
    levelOptions() {
      return [
        { value: null, label: this.$t('roleMatrix_levelNone') },
        { value: 'viewer', label: this.levelLabels.viewer },
        { value: 'editor', label: this.levelLabels.editor },
      ];
    },
    visibleRows() {
      return getVisibleRows(this.catalog, this.collapsed);
    },
    focusedRole() {
      return this.roles.find((role) => role.number === this.focus.roleNumber) || null;
    },
    focusedEffectiveLevel() {
      if (!this.focusedRole || !this.focus.nodeId) {
        return null;
      }
      return getEffectiveLevel(
        this.assignmentsOf(this.focusedRole),
        this.catalog,
        this.focus.nodeId
      );
    },
    previewRows() {
      if (!this.focusedRole) {
        return [];
      }
      return getMenuPreviewRows(this.assignmentsOf(this.focusedRole), this.catalog);
    },
    changedRoles() {
      return this.roles.filter(
        (role) =>
          diffRoleAssignments(this.savedAssignmentsOf(role), this.assignmentsOf(role)).changed
      );
    },
    pendingChangeCount() {
      return this.changedRoles.length;
    },
    saveButtonLabel() {
      if (this.pendingChangeCount === 0) {
        return this.$t('roleMatrix_saved');
      }
      return this.$t('roleMatrix_saveChanges', { count: this.pendingChangeCount });
    },
  },
  async mounted() {
    try {
      await Promise.all([this.fetchAccessCatalog(), this.fetchOrganizationRoles()]);
      this.resetEditedAssignments();
      if (this.roles.length > 0) {
        this.focusRole(this.roles[0].number);
      }
      await this.fetchUsers();
    } catch (error) {
      this.showError(error);
    }
  },
  methods: {
    ...mapActions([
      'fetchAccessCatalog',
      'fetchOrganizationRoles',
      'fetchUsers',
      'createOrganizationRole',
      'updateOrganizationRole',
      'deleteOrganizationRole',
      'setOrganizationRoleMembers',
      'setOrganizationRoleAssignments',
      'refreshMyAccess',
    ]),
    organizationIdOf(user) {
      return user && user.clients && user.clients[0] ? user.clients[0].id : null;
    },
    /** Stored assignments on nodes the catalog no longer knows stay on the server untouched. */
    savedAssignmentsOf(role) {
      return role.assignments
        .filter((assignment) => this.catalogNodeIds.has(assignment.node))
        .map(({ node, level }) => ({ node, level }));
    },
    assignmentsOf(role) {
      return this.editedAssignments[role.number] || this.savedAssignmentsOf(role);
    },
    resetEditedAssignments() {
      this.editedAssignments = Object.fromEntries(
        this.roles.map((role) => [role.number, this.savedAssignmentsOf(role)])
      );
    },
    setAssignments(roleNumber, assignments) {
      this.editedAssignments = { ...this.editedAssignments, [roleNumber]: assignments };
    },
    cellState(role, nodeId, level) {
      return getCellState(this.assignmentsOf(role), this.catalog, nodeId, level);
    },
    cellTitle(role, node, level) {
      const stateLabel = this.$t(CELL_STATE_LABEL_KEYS[this.cellState(role, node.id, level)]);
      return `${role.name} · ${this.nodeLabel(node)} · ${this.levelLabels[level]} — ${stateLabel}`;
    },
    cellHint(role, nodeId) {
      return isMissingViewAssignment(this.assignmentsOf(role), this.catalog, nodeId)
        ? this.$t('roleMatrix_blockNeedsView')
        : '';
    },
    nodeLabel(node) {
      return this.$t(node.labelKey);
    },
    kindLabel(node) {
      return this.$t(`roleMatrix_kind_${node.kind}`);
    },
    toggleCollapse(nodeId) {
      this.collapsed = { ...this.collapsed, [nodeId]: !this.collapsed[nodeId] };
    },
    focusRole(roleNumber) {
      this.focus = { ...this.focus, roleNumber };
    },
    focusCell({ roleNumber, nodeId }) {
      this.focus = { roleNumber, nodeId };
    },
    toggleCell({ roleNumber, nodeId, level }) {
      const role = this.roles.find((entry) => entry.number === roleNumber);
      this.setAssignments(
        roleNumber,
        toggleCellLevel(this.assignmentsOf(role), this.catalog, nodeId, level)
      );
      this.focusCell({ roleNumber, nodeId });
    },
    setFocusedLevel(level) {
      if (!this.focusedRole || !this.focus.nodeId) {
        return;
      }
      this.setAssignments(
        this.focusedRole.number,
        applyCellLevelChange(
          this.assignmentsOf(this.focusedRole),
          this.catalog,
          this.focus.nodeId,
          level
        )
      );
    },
    async save() {
      this.isSaving = true;
      try {
        for (const role of this.changedRoles) {
          await this.setOrganizationRoleAssignments({
            number: role.number,
            assignments: this.assignmentsOf(role),
          });
        }
        this.resetEditedAssignments();
        await this.refreshMyAccess();
      } catch (error) {
        this.showError(error);
      } finally {
        this.isSaving = false;
      }
    },
    openCreateRoleDialog() {
      this.roleDialog = { isVisible: true, mode: 'create', role: null };
    },
    openRenameRoleDialog(role) {
      this.roleDialog = { isVisible: true, mode: 'rename', role };
    },
    openMembersDialog(role) {
      this.membersDialog = { isVisible: true, role };
    },
    async submitRoleDialog(name) {
      try {
        if (this.roleDialog.mode === 'create') {
          const role = await this.createOrganizationRole({ name });
          this.setAssignments(role.number, []);
          this.focusRole(role.number);
          return;
        }
        const { role } = this.roleDialog;
        await this.updateOrganizationRole({
          number: role.number,
          role: { name, description: role.description },
        });
      } catch (error) {
        this.showError(error);
      }
    },
    async deleteRole() {
      const { role } = this.roleDialog;
      try {
        await this.deleteOrganizationRole(role.number);
        const remainingAssignments = { ...this.editedAssignments };
        delete remainingAssignments[role.number];
        this.editedAssignments = remainingAssignments;
        if (this.focus.roleNumber === role.number) {
          this.focusRole(this.roles[0] ? this.roles[0].number : null);
        }
      } catch (error) {
        this.showError(error);
      }
    },
    async submitMembers(userIds) {
      try {
        await this.setOrganizationRoleMembers({
          number: this.membersDialog.role.number,
          userIds,
        });
        await this.refreshMyAccess();
      } catch (error) {
        this.showError(error);
      }
    },
    showError(error) {
      console.error('Role matrix:', error);
      this.$toast.add({
        severity: 'error',
        summary: this.$t('ERROR'),
        detail: error.message,
        life: TOAST_LIFE_MS,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.role-matrix {
  overflow: hidden;
  border: 1px solid $gray-200;
  border-radius: 8px;
  background-color: $white;
  box-shadow: 0 1px 2px rgba($black, 0.05);
}

.role-matrix__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 2px solid $gray-200;
}

.role-matrix__eyebrow {
  margin: 0;
  color: $gray-600;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.role-matrix__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.2;
}

.role-matrix__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.role-matrix__notice {
  margin: 0;
  padding: 8px 20px;
  border-bottom: 1px solid $gray-200;
  color: $gray-600;
  font-size: 0.875rem;
}

.role-matrix__body {
  display: flex;
}

.role-matrix__matrix {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
}

.role-matrix__rows {
  overflow-y: auto;
}
</style>
